<template>
    <div>
        <div v-if="navigationPending" class="navigation-bootstrap" aria-live="polite">
            <img src="/icons/icon-192.png" alt="" class="navigation-bootstrap__icon">
            <span>Загружаем рабочее пространство…</span>
        </div>
        <modern-shell v-else-if="isAuthorized && !isAuthPage" @logout="logout">
            <router-view :key="routerViewKey"/>
        </modern-shell>
        <div v-else-if="isAuthPage" class="container-fluid auth-page-container">
            <div class="row auth-page-row">
                <main role="main" class="col-12 px-0 main-parent main-parent--auth">
                    <router-view :key="routerViewKey"/>
                </main>
            </div>
        </div>
        <div v-else class="navigation-bootstrap" role="status">Сеанс завершён. <a href="/login">Войти</a></div>
        <create-material-target/>
        <error-modal/>
        <lifecycle-abort-modal/>
    </div>
</template>

<script>
import CreateMaterialTarget from "../Materials/Targets/CreateTarget/CreateMaterialTarget.vue";
import ErrorModal from "../../components/Modals/ErrorModal.vue";
import LifecycleAbortModal from "../../components/Modals/LifecycleAbortModal.vue";
import ModernShell from "./ModernShell.vue";
import {logoutSession, lockSession} from '@/utils/sessionSecurity';

export default {
    name: "MainComponent",
    components: { CreateMaterialTarget, ErrorModal, LifecycleAbortModal, ModernShell },
    data() {
        return { navigationSettingsPromise: null };
    },
    computed: {
        isAuthorized() { return this.$store.getters.isAuthorized; },
        navigationExperience() { return this.$store.state.navigationExperience || {}; },
        navigationPending() {
            return this.isAuthorized && !this.isAuthPage && this.navigationExperience.loaded !== true;
        },
        userIdentity() {
            const user = this.$store.getters.currentUser;
            return user && user.userId ? user.userId : null;
        },
        isAuthPage() { return this.$route.matched.some(({meta}) => meta.standalone === true); },
        /**
         * Иначе при router.replace /requests?highlight= → /requests меняется fullPath,
         * Vue пересоздаёт вложенный компонент (двойные запросы и сброс скролла).
         * Служебный query highlight для списков заявок, типов заявок, механиков, объектов, сотрудников и отчётов в ключ не входит.
         */
        routerViewKey() {
            const path = this.$route.path;
            // These workspaces handle route changes and pending saves themselves.
            if (/^\/timesheets(?:\/\d+)?$/.test(path)) return 'timesheet-workspace';
            if (path === '/payroll') return 'payroll-workspace';
            if (path === '/requests' || path === '/requests/types' || path === '/mechanic-assets' || path === '/objects' || path === '/users' || path === '/employees' || path === '/reports') {
                const q = { ...this.$route.query };
                delete q.highlight;
                if (path === '/requests') {
                    // Requests.vue синхронизирует эти фильтры с route сам.
                    // Не пересоздаём весь список и не делаем повторные запросы.
                    delete q.tab;
                    delete q.request_type_id;
                }
                const keys = Object.keys(q).sort();
                if (!keys.length) {
                    return path;
                }
                const qs = keys.map((k) => `${k}=${q[k]}`).join('&');
                return `${path}?${qs}`;
            }
            return this.$route.fullPath;
        }
    },
    watch: {
        '$route.fullPath'() {
            if (this.navigationPending) this.loadNavigationSettings();
        },
        isAuthorized(value) {
            if (value) this.loadNavigationSettings();
        },
        userIdentity(value, oldValue) {
            if (value && value !== oldValue) this.loadNavigationSettings();
        }
    },
    mounted() { this.loadNavigationSettings(); },
    methods: {
        async loadNavigationSettings() {
            if (!this.isAuthorized || this.isAuthPage) return;
            if (this.navigationSettingsPromise) return this.navigationSettingsPromise;
            const request = this.$store.dispatch('navigation_loadSettings', {silent: true});
            this.navigationSettingsPromise = request;
            try { return await request; }
            finally {
                if (this.navigationSettingsPromise === request) this.navigationSettingsPromise = null;
            }
        },
        async logout() {
            try { await logoutSession('staff'); }
            catch (error) { lockSession('staff', error); return; }
            this.$store.commit('removeRefreshToken');
            this.$store.commit('removeToken');
            this.$store.dispatch('setAuthorizationHeader', null);
            if (this.$ability) this.$ability.update([]);
            // End the document as well as the session: route/draft guards must not
            // leave a confidential workspace mounted after explicit logout.
            window.location.replace('/login');
        }
    }
};
</script>

<style scoped lang="stylus">
.navigation-bootstrap
  display flex
  min-height 100vh
  min-height 100dvh
  align-items center
  justify-content center
  flex-direction column
  gap 12px
  color #5f6b7a
  background #f4f7fb
  font-size 14px

.navigation-bootstrap__icon
  width 54px
  height 54px
  border-radius 15px
  box-shadow 0 8px 24px rgba(34, 75, 126, .14)

.main-parent
  min-width 0

.auth-page-container
  padding-left 0
  padding-right 0

.auth-page-row
  margin-left 0
  margin-right 0

.main-parent--auth
  min-height 100vh
  padding-top 0
</style>
